import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const BOIRPenalties = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Know about FinCEN BOIR Penalties"
          description="Learn about the BOIR penalties and stay compliant with FinCEN. Submit your Report now with TaxZerone before the deadline"
          keywords=""
          canonical="resources/fincen-boir/boir-penalties/"
          ogType="article"
          jsonLdPricing="39.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Alert
          severity="warning"
          icon={<WarningOutlinedIcon sx={{ fontSize: 44 }} />}
          sx={{ borderRadius: 3, mt: 3 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              component="div"
              variant="body1"
              sx={{
                textAlign: { xs: 'left', sm: 'left' },
                fontSize: { xs: 13, sm: 14, md: 15 },
                //  color: '#ccc',
                lineHeight: 1.3,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700, mb: 0 }}>
                BOI Compliance Update:
              </AlertTitle>
              No reporting is required for U.S. businesses and citizens. Only
              Foreign Reporting Companies must comply.{' '}
              <Link
                href="https://blog.taxzerone.com/posts/boi-report-fincen-revises-reporting-rules-now-focused-on-foreign-entities/"
                target="_blank"
                sx={{ color: '#0202ff' }}
              >
                See Details
              </Link>
              .
            </Typography>
          </Stack>
        </Alert>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          BOI Report:{' '}
          <span className="highlight-container">
            <span className="highlight"> Penalties and Charges</span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Know about the penalties and Charges associated with BOI Report
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirpenalties`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs
            items={[
              { label: 'Essential Forms', path: '/resources/essential-forms/' },
            ]}
          />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Why is Filing a BOI Report Important?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Under the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Corporate Transparency Act
                  </span>
                  , businesses are required to report information about
                  individuals who own or control the company as{' '}
                  <Link href="/efile-boi-report/" sx={{ color: '#0202ff' }}>
                    BOI reports
                  </Link>{' '}
                  to FinCEN. Missing the deadline or filing inaccurate reports
                  can result in serious penalties. For detailed guidance, refer
                  to the{' '}
                  <Link
                    href="/resources/fincen-boir/boi-reporting-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    General Instructions for BOI Reporting
                  </Link>
                  .
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When can FinCEN Penalize an Individual or Organization?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  FinCEN may impose penalties under the following circumstances:
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Failure to File:</b> Not submitting the BOI Report by
                    the required{' '}
                    <Link
                      href="/resources/fincen-boir/boi-reporting-due-dates/"
                      sx={{ color: '#0202ff' }}
                    >
                      deadline
                    </Link>
                    .
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Failure to Correct Information:</b> Not amending
                    incorrect information within 30 days of receiving a notice
                    from FinCEN.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Failure to Update Information:</b> Not updating details
                    within 30 days of changes in beneficial ownership.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Willful Non-Compliance:</b>
                  </li>
                  <ul style={{ listStyleType: 'disc' }}>
                    <li style={{ marginBottom: 12 }}>
                      The individual responsible for filing the BOI Report fails
                      to comply with the requirement to file it.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The individual responsible for filing the BOI Report
                      intentionally provides false information.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The{' '}
                      <Link
                        href="/resources/fincen-boir/company-applicant-vs-beneficial-owner/"
                        sx={{ color: '#0202ff' }}
                      >
                        Beneficial Owner or Company Applicant
                      </Link>{' '}
                      delays providing necessary information to the filer,
                      leading to filing delays.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      The Beneficial Owner or Company Applicant provides
                      incorrect information to the filer, resulting in an
                      inaccurate BOI Report.
                    </li>
                  </ul>
                </ol>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        Submit Your BOI Report Now with TaxZerone
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 160, textAlign: 'center' }}
                      id="common"
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirpenalties`}
                      >
                        Report Now!
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who is liable for violating the BOI Report requirement?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  FinCEN can hold the following individuals and entities
                  accountable for non-compliance:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>Filers:</b> Individuals responsible for submitting the
                    BOI Report on behalf of the entity.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Company Applicants:</b> Individuals responsible for
                    providing required details to the filer.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Beneficial Owners:</b> Individuals who must provide
                    accurate information about their ownership or control.
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What penalties can FinCEN impose?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  FinCEN can enforce both civil and criminal penalties to ensure
                  compliance with BOI reporting requirements. These penalties
                  highlight the importance of meeting responsibilities and
                  encourage individuals and organizations to stay compliant.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    color: '#333333',
                    mb: 1,
                  }}
                >
                  Civil penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Civil Penalties apply to individuals or entities that
                  willfully violate BOI reporting rules. The fine can reach up
                  to $500 per day for each day the violation continues. This
                  amount is adjusted annually for inflation(For example: The
                  current penalty is $591), ensuring the penalties remain
                  impactful over time. Non-compliance due to negligence or
                  oversight can quickly become costly, emphasizing the
                  importance of timely and accurate reporting.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    color: '#333333',
                    mb: 1,
                  }}
                >
                  Criminal penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Criminal penalties are more severe and are imposed in cases of
                  intentional violations. Individuals violating BOIR
                  requirements may face up to two years imprisonment or a fine
                  of up to $10,000 or both. This applies to those who willfully
                  fail to file a BOI report, provide false information, or
                  neglect to update or correct previously submitted details.
                  These penalties highlight FinCEN&apos;s commitment to
                  upholding the integrity of beneficial ownership reporting and
                  deterring fraudulent activity.
                </Typography>
                {/* <Box
                  component={'div'}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Avoid Penalties—File Your BOI Report with TaxZerone!
                    </Typography>
                    <Typography variant="body2">
                      File your BOI Report quickly and affordably with TaxZerone
                      for just <b>$39.99</b> per report.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirpenalties`}
                    >
                      Submit Now!
                    </Button>
                  </Box>
                </Box> */}

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Disclaimer, source, and resources
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above details are for information purposes only.
                        This should not be used as the evidence or reference in
                        any court of law or legal proceedings.{' '}
                        <br style={{ marginBottom: 8 }} />
                        The information given in the article is taken from the
                        official website of FinCEN - A Federal Government
                        Agency.
                        <ul>
                          <li>
                            <Link
                              href="https://www.fincen.gov/boi"
                              sx={{ color: '#0202ff' }}
                              target="_blank"
                            >
                              FinCEN
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="https://www.fincen.gov/boi-faqs"
                              sx={{ color: '#0202ff' }}
                              target="_blank"
                            >
                              FinCEN FAQs
                            </Link>
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Avoid Penalties—File Your BOI Report with TaxZerone!
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your BOI Report quickly and affordably with TaxZerone for
              just <b>$39.99</b> per report.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirpenalties`}
              >
                Submit Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default BOIRPenalties;
